import { getPostHogStorageKeys, disablePostHog } from '../hooks/usePostHog';
import { CANNY_ENABLED, getCannyStorageKeys, clearCannyStorage } from '../hooks/useCanny';

/**
 * Dynamically load external script
 * @param {string} src - Script source URL
 * @param {Object} options - Script attributes
 * @returns {Promise} Resolves when script is loaded
 */
export const loadScript = (src, options = {}) => {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            resolve();
            return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        Object.keys(options).forEach(key => {
            script[key] = options[key];
        });

        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

        document.head.appendChild(script);
    });
};

/**
 * Load PostHog script dynamically
 * @returns {Promise} Resolves when PostHog stub is loaded
 */
export const loadPostHogScript = () => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            resolve();
            return;
        }

        if (window.posthog) {
            resolve();
            return;
        }

        const script = `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys getNextSurveyStep onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);`;

        const scriptElement = document.createElement('script');
        scriptElement.textContent = script;
        scriptElement.setAttribute('data-posthog-stub', 'true');
        document.head.appendChild(scriptElement);

        const checkPostHog = setInterval(() => {
            if (window.posthog) {
                clearInterval(checkPostHog);
                resolve();
            }
        }, 50);

        setTimeout(() => {
            clearInterval(checkPostHog);
            resolve();
        }, 2000);
    });
};

/**
 * Load Canny script dynamically
 * @returns {Promise} Resolves when script is loaded
 */
export const loadCannyScript = () => {
    // Hard stop: never fetch sdk.js from canny.io while the widget is off.
    if (!CANNY_ENABLED) {
        return Promise.resolve();
    }

    return loadScript('https://canny.io/sdk.js');
};

/**
 * Delete a cookie across all relevant path/domain combinations.
 */
const nukeCookie = (cookieName) => {
    const paths = ['/', '', '/app', '/login'];
    const hostname = window.location.hostname;
    const domains = ['', hostname, `.${hostname}`];

    const parts = hostname.split('.');
    if (parts.length > 1) {
        const rootDomain = parts.slice(-2).join('.');
        domains.push(rootDomain, `.${rootDomain}`);
    }
    if (parts.length > 2) {
        domains.push(parts.slice(-3).join('.'), `.${parts.slice(-3).join('.')}`);
    }

    paths.forEach(path => {
        domains.forEach(domain => {
            const pathPart = path ? `; path=${path}` : '';
            const domainPart = domain ? `; domain=${domain}` : '';

            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC${pathPart}${domainPart}`;
            document.cookie = `${cookieName}=; max-age=0${pathPart}${domainPart}`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=Lax`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=None; Secure`;
        });
    });
};

/**
 * Remove PostHog scripts from the DOM and wipe all PostHog storage.
 * disablePostHog() is synchronous and disables persistence first, so no new
 * cookies can be written between the disable call and the cleanup below.
 */
export const removePostHogScripts = () => {
    disablePostHog();

    document.querySelector('script[data-posthog-stub="true"]')?.remove();
    document.querySelectorAll('script[src*="posthog"]').forEach(s => s.remove());

    const posthogKeys = getPostHogStorageKeys();
    posthogKeys.localStorage.forEach(key => localStorage.removeItem(key));
    posthogKeys.sessionStorage.forEach(key => sessionStorage.removeItem(key));

    const phPatterns = ['ph_', 'ph-', 'posthog', '__ph'];
    document.cookie.split(';').forEach(c => {
        const name = c.trim().split('=')[0];
        if (phPatterns.some(p => name.toLowerCase().includes(p.toLowerCase()))) {
            nukeCookie(name);
        }
    });
};

/**
 * Remove Canny scripts from the DOM and wipe all Canny storage.
 */
export const removeCannyScripts = () => {
    document.querySelectorAll('script[src*="canny.io"]').forEach(s => s.remove());

    clearCannyStorage();

    if (window.Canny) {
        delete window.Canny;
    }

    const cannyPatterns = ['canny', '__canny'];
    document.cookie.split(';').forEach(c => {
        const name = c.trim().split('=')[0];
        if (cannyPatterns.some(p => name.toLowerCase().includes(p.toLowerCase()))) {
            nukeCookie(name);
        }
    });
};

/**
 * Remove all analytics scripts (PostHog + Canny) from DOM and clean storage.
 */
export const removeAnalyticsScripts = () => {
    removePostHogScripts();
    removeCannyScripts();
};

/**
 * Get audit of all analytics-related storage
 */
export const auditAnalyticsStorage = () => {
    return {
        posthog: {
            localStorage: getPostHogStorageKeys(),
            cookies: document.cookie.split(';')
                .map(c => c.trim().split('=')[0])
                .filter(name => name.startsWith('ph_'))
        },
        canny: {
            localStorage: getCannyStorageKeys(),
            cookies: document.cookie.split(';')
                .map(c => c.trim().split('=')[0])
                .filter(name => name.includes('canny'))
        }
    };
};